import React, { useState, useEffect } from 'react';
import BaseForm from './BaseForm';
import FormField from './FormField';
import SymptomsSection from './SymptomsSection';
import CallRulesSection from './CallRulesSection';
import { medicalCenterService } from '../../services/medicalCenterService';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { ENROLLMENT_VALIDATION } from '../../constants/validationRules';
import {
  ONBOARDING_PROGRAM_CODE,
  getProgramConfig,
  extractSymptomsFromProgram,
  buildInitialSymptoms,
  convertRuleToApiFormat,
} from '../../utils/enrollmentUtils';

const EnrollmentForm = ({ isOpen, onClose, onSubmit, loading = false, enrollment = null, patients = [] }) => {
  const isEditMode = !!enrollment;
  const { medicalCenterId } = useMedicalCenter();

  const [programs, setPrograms] = useState([]);
  const [programSymptoms, setProgramSymptoms] = useState([]);
  const [programName, setProgramName] = useState('');
  const [selectedProgramCode, setSelectedProgramCode] = useState('');

  const programConfig = getProgramConfig(selectedProgramCode);

  const getTodayString = () => new Date().toISOString().split('T')[0];

  const getInitialFormData = () => ({
    patient_id: '',
    program_id: '',
    start_date: getTodayString(),
    end_date: '',
    call_rules: [{ ...programConfig.defaultCallRule }],
    list_of_symptoms_to_ask: {},
    device_id: ''
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) return;
    const loadPrograms = async () => {
      if (!medicalCenterId) return;
      const mc = await medicalCenterService.getMedicalCenter(medicalCenterId);
      setPrograms(mc?.programs || []);
    };
    loadPrograms();
  }, [medicalCenterId, isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    if (!formData.program_id) {
      setProgramSymptoms([]);
      setSelectedProgramCode('');
      return;
    }
    const selectedProgram = programs.find(p => p.id === formData.program_id);
    const code = selectedProgram?.code || '';
    setSelectedProgramCode(code);

    const config = getProgramConfig(code);
    const symptoms = extractSymptomsFromProgram(selectedProgram);
    setProgramSymptoms(symptoms);

    setFormData(prev => ({
      ...prev,
      list_of_symptoms_to_ask: buildInitialSymptoms(symptoms),
      device_id: '',
      call_rules: [{ ...config.defaultCallRule }]
    }));
  }, [formData.program_id, programs, isEditMode]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      setFormData(getInitialFormData());
      setProgramSymptoms([]);
      setProgramName('');
      setSelectedProgramCode('');
      setErrors({});
    }
  }, [isOpen, isEditMode]);

  useEffect(() => {
    const initEditForm = async () => {
      if (!enrollment || !isOpen || !medicalCenterId) return;

      const mc = await medicalCenterService.getMedicalCenter(medicalCenterId);
      const allPrograms = mc?.programs || [];
      const matched = allPrograms.find(p => p.id === enrollment.program?.id);
      const code = matched?.code || enrollment.program?.code || '';

      setProgramName(matched?.name || enrollment.program?.id || 'Unknown');
      setSelectedProgramCode(code);

      const symptoms = extractSymptomsFromProgram(matched);
      setProgramSymptoms(symptoms);

      const initialSymptoms = buildInitialSymptoms(symptoms, enrollment.list_of_symptoms_to_ask);

      const config = getProgramConfig(code);
      const convertedCallRules = (enrollment.call_rules || []).map(rule => {
        let hour12 = parseInt(rule.hour);
        let period = 'AM';

        if (hour12 === 0) {
          hour12 = 12;
          period = 'AM';
        } else if (hour12 === 12) {
          period = 'PM';
        } else if (hour12 > 12) {
          hour12 -= 12;
          period = 'PM';
        }

        return {
          frequency: rule.frequency || 'weekly',
          interval: rule.interval || 1,
          days: rule.days || (rule.day ? [rule.day] : ["monday"]),
          days_of_month: rule.days_of_month || [],
          date: rule.date || '',
          hour: hour12,
          minute: parseInt(rule.minute),
          period: period,
        };
      });

      setFormData({
        patient_id: enrollment.patient?.id || '',
        program_id: enrollment.program?.id || '',
        start_date: enrollment.start_date || '',
        end_date: enrollment.end_date || '',
        call_rules: convertedCallRules.length > 0 ? convertedCallRules : [{ ...config.defaultCallRule }],
        list_of_symptoms_to_ask: initialSymptoms,
        device_id: enrollment.device_id || '',
      });
      setErrors({});
    };

    if (isEditMode) {
      initEditForm();
    }
  }, [enrollment, isOpen, medicalCenterId, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'device_id') {
      const sanitized = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
      setFormData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSymptomChange = (symptomCode, checked) => {
    setFormData(prev => ({
      ...prev,
      list_of_symptoms_to_ask: { ...prev.list_of_symptoms_to_ask, [symptomCode]: checked },
    }));
  };

  const handleCallRuleChange = (index, field, value) => {
    const newCallRules = [...formData.call_rules];
    newCallRules[index] = { ...newCallRules[index], [field]: value };
    setFormData(prev => ({ ...prev, call_rules: newCallRules }));
  };

  const addCallRule = () => {
    setFormData(prev => ({
      ...prev,
      call_rules: [...prev.call_rules, { ...programConfig.defaultCallRule }]
    }));
  };

  const removeCallRule = (index) => {
    if (formData.call_rules.length > 1) {
      setFormData(prev => ({
        ...prev,
        call_rules: prev.call_rules.filter((_, i) => i !== index),
      }));
    }
  };

  const validateForm = () => {
    const context = { isOnboardingProgram: !programConfig.hasSymptoms };
    const newErrors = {};

    Object.keys(ENROLLMENT_VALIDATION).forEach(field => {
      const rule = ENROLLMENT_VALIDATION[field];
      const value = formData[field];
      if (rule.required && !value) {
        newErrors[field] = rule.message || `${field} is required`;
      } else if (rule.custom) {
        const error = rule.custom(value, formData, context);
        if (error) newErrors[field] = error;
      }
    });

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const enrollmentData = {
      ...formData,
      call_rules: formData.call_rules.map(convertRuleToApiFormat),
    };

    if (!programConfig.hasSymptoms) {
      enrollmentData.list_of_symptoms_to_ask = {};
      enrollmentData.device_id = formData.device_id;
    } else {
      const allSymptoms = {};
      programSymptoms.forEach(s => {
        allSymptoms[s.code] = formData.list_of_symptoms_to_ask[s.code] || false;
      });
      enrollmentData.list_of_symptoms_to_ask = allSymptoms;
      delete enrollmentData.device_id;
    }

    onSubmit(enrollmentData);
  };

  const handleClose = () => {
    setProgramSymptoms([]);
    setProgramName('');
    setSelectedProgramCode('');
    setErrors({});
    onClose();
  };

  const currentPatient = isEditMode ? enrollment?.patient : null;
  const showDetails = isEditMode || !!formData.program_id;

  return (
    <BaseForm
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={isEditMode ? "Edit Enrollment" : "Enroll Patient"}
      loading={loading}
      submitText={isEditMode ? "Update Enrollment" : "Enroll Patient"}
      size="lg"
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Enrollment Details</h3>

          {isEditMode ? (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Patient</label>
              <div className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700">
                {currentPatient
                  ? `${currentPatient.first_name} ${currentPatient.last_name} (${currentPatient.email})`
                  : 'Patient not found'}
              </div>
            </div>
          ) : (
            <FormField
              label="Patient"
              name="patient_id"
              value={formData.patient_id}
              onChange={handleChange}
              required
              error={errors.patient_id}
              options={patients.map(p => ({
                value: p.id,
                label: `${p.first_name} ${p.last_name} (${p.email})`,
              }))}
            />
          )}

          {isEditMode ? (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Program</label>
              <div className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700">
                {programName || 'Unknown'}
              </div>
              <p className="text-xs text-gray-500">
                Program cannot be changed when editing an enrollment
              </p>
            </div>
          ) : (
            <FormField
              label="Program"
              name="program_id"
              value={formData.program_id}
              onChange={handleChange}
              required
              options={programs.map(p => ({
                value: p.id,
                label: p.name,
              }))}
            />
          )}
        </div>

        {showDetails ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                required
                error={errors.start_date}
              />
              <FormField
                label="End Date"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                required
                error={errors.end_date}
              />
            </div>

            <CallRulesSection
              callRules={formData.call_rules}
              onCallRuleChange={handleCallRuleChange}
              onAddRule={addCallRule}
              onRemoveRule={removeCallRule}
              error={errors.call_rules}
            />

            {programConfig.hasSymptoms && (
              <SymptomsSection
                symptoms={formData.list_of_symptoms_to_ask}
                onSymptomChange={handleSymptomChange}
                availableSymptoms={programSymptoms}
              />
            )}

            {programConfig.requiresDeviceId && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Device Information</h3>
                <p className="text-sm text-gray-600">
                  Enter the 4-character Hemotag device identifier for this patient.
                </p>
                <FormField
                  label="Device ID"
                  name="device_id"
                  type="text"
                  value={formData.device_id}
                  onChange={handleChange}
                  required
                  error={errors.device_id}
                  maxLength={4}
                  placeholder="e.g. A1B2"
                />
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
            Please select a program to continue with enrollment details.
          </div>
        )}
      </div>
    </BaseForm>
  );
};

export default EnrollmentForm;